<template>
  <div class="cookie-settings-panel" v-if="showSettings">
    <div 
      class="settings-item" 
      v-for="category in categories" 
      :key="category.id"
    >
      <!-- 左側：固定啟用標籤 / 開關 | 右側：名稱 + 展開按鈕 + 描述 -->
      <div class="settings-item-layout">
        <!-- 左側區塊 -->
        <div class="settings-item-left">
          <label v-if="category.locked" class="settings-always-on">
            {{ $t('cookieConsent.categoryAlwaysOn') }}
          </label>
          <label v-else class="toggle-switch">
            <input 
              type="checkbox" 
              v-model="localPreferences[category.id]"
              @change="handleToggle(category.id)"
            />
            <span class="toggle-slider"></span>
          </label>
        </div>

        <!-- 右側區塊 -->
        <div class="settings-item-right">
          <!-- 標題列 -->
          <div class="settings-item-header" @click="toggleExpand(category.id)">
            <span class="settings-item-name">{{ $t(`cookieConsent.category.${category.id}.name`) }}</span>
            <button 
              class="expand-btn"
              :class="{ 'is-expanded': expandedCategory === category.id }"
            >
              <span class="expand-icon"></span>
            </button>
          </div>

          <!-- 展開的描述內容 -->
          <div 
            class="settings-item-description-wrapper"
            v-show="expandedCategory === category.id"
          >
            <p class="settings-item-description">
              {{ $t(`cookieConsent.category.${category.id}.description`) }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { COOKIE_CATEGORIES } from '@/store/modules/cookieConsent';

export default {
  name: 'CookieSettingsPanel',
  
  setup() {
    const store = useStore();

    // Cookie categories configuration
    const categories = [
      {
        id: COOKIE_CATEGORIES.STRICTLY_NECESSARY,
        locked: true
      },
      {
        id: COOKIE_CATEGORIES.PERFORMANCE,
        locked: false
      },
      {
        id: COOKIE_CATEGORIES.FUNCTIONAL,
        locked: false
      },
      {
        id: COOKIE_CATEGORIES.TARGETING,
        locked: false
      }
    ];

    // Track which category is expanded (accordion - only one at a time)
    const expandedCategory = ref(null);

    // Local state for toggle controls
    const localPreferences = ref({
      [COOKIE_CATEGORIES.STRICTLY_NECESSARY]: true,
      [COOKIE_CATEGORIES.FUNCTIONAL]: false,
      [COOKIE_CATEGORIES.PERFORMANCE]: false,
      [COOKIE_CATEGORIES.TARGETING]: false
    });

    // Sync with Vuex state on mount
    const syncFromStore = () => {
      const state = store.state.cookieConsent?.preferences || {};
      localPreferences.value = {
        [COOKIE_CATEGORIES.STRICTLY_NECESSARY]: state.strictly_necessary ?? true,
        [COOKIE_CATEGORIES.FUNCTIONAL]: state.functional ?? false,
        [COOKIE_CATEGORIES.PERFORMANCE]: state.performance ?? false,
        [COOKIE_CATEGORIES.TARGETING]: state.targeting ?? false
      };
    };

    // Watch for settings panel expansion to sync state
    const showSettings = computed(() => store.state.cookieConsent?.isSettingsExpanded);

    // Toggle expand/collapse for a category
    const toggleExpand = (categoryId) => {
      expandedCategory.value = expandedCategory.value === categoryId ? null : categoryId;
    };

    // Handle toggle change
    const handleToggle = (categoryId) => {
      if (categoryId === COOKIE_CATEGORIES.STRICTLY_NECESSARY) {
        localPreferences.value[categoryId] = true;
      }
    };

    // Select all toggles (without saving) - used by "Accept All" in manage settings
    const selectAllToggles = () => {
      localPreferences.value = {
        [COOKIE_CATEGORIES.STRICTLY_NECESSARY]: true,
        [COOKIE_CATEGORIES.FUNCTIONAL]: true,
        [COOKIE_CATEGORIES.PERFORMANCE]: true,
        [COOKIE_CATEGORIES.TARGETING]: true
      };
    };

    // Save preferences - 與儲存設定按鈕功能相同
    const handleSave = () => {
      store.dispatch('cookieConsent/updatePreferences', {
        functional: localPreferences.value[COOKIE_CATEGORIES.FUNCTIONAL],
        performance: localPreferences.value[COOKIE_CATEGORIES.PERFORMANCE],
        targeting: localPreferences.value[COOKIE_CATEGORIES.TARGETING]
      });
    };

    // Sync on mount
    syncFromStore();

    return {
      categories,
      localPreferences,
      showSettings,
      expandedCategory,
      toggleExpand,
      handleToggle,
      selectAllToggles,
      handleSave
    };
  }
};
</script>

<style scoped>
/* Settings Panel Container - 無底色框 */
.cookie-settings-panel {
  margin: 0 0 0 0;
  padding: 0;
}

/* Individual Settings Item */
.settings-item {
  position: relative;
  margin-bottom: 12px;
  padding-left: 130px;
}

.settings-item:last-of-type {
  margin-bottom: 0;
}

/* 左側區塊 - 自適應寬度，絕對定位不影響右側 */
.settings-item-left {
  position: absolute;
  left: 0;
  top: 0;
  height: 28px;
  display: flex;
  align-items: center;
}

/* 右側區塊 - 移除固定 margin，保持標準 flex */
.settings-item-right {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.settings-item:last-of-type {
  margin-bottom: 0;
}

/* 左右兩欄佈局 */
.settings-item-layout {
  display: flex;
  align-items: flex-start;
}

/* 左側區塊 - 固定寬度確保右側文字對齊 */
.settings-item-left {
  flex-shrink: 0;
  width: 110px;
  height: 28px;
  display: flex;
  align-items: center;
}

/* 右側區塊 - 移除 margin-left，保持標準 flex 佈局 */
.settings-item-right {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* 標題列 - 可點擊展開，與右側容器高度相同 */
.settings-item-header {
  display: flex;
  align-items: center;
  height: 28px;
  cursor: pointer;
}

/* Settings Item Name */
.settings-item-name {
  flex: 1;
  color: #fff;
  font-family: 'Inter', var(--cjk-font), sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  margin-right: 8px;
}

/* 加號/減號展開按鈕 */
.expand-btn {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-left: auto;
}

.expand-icon {
  position: relative;
  width: 12px;
  height: 12px;
}

/* 水平線 */
.expand-icon::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1.5px;
  background: #fff;
  transform: translateY(-50%);
}

/* 垂直線 - 收合時顯示（+），展開時隱藏（−） */
.expand-icon::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 1.5px;
  background: #fff;
  transform: translateX(-50%);
  transition: opacity 0.2s ease;
}

.expand-btn.is-expanded .expand-icon::after {
  opacity: 0;
}

/* 展開描述區域 - 右側區塊內 */
.settings-item-description-wrapper {
  margin-top: 8px;
}

/* 描述文字 + 左側垂直線 */
.settings-item-description {
  position: relative;
  padding-left: 12px;
  color: rgba(255, 255, 255, 0.7);
  font-family: 'Inter', var(--cjk-font), sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  margin: 0;
  text-align: left;
}

/* 垂直線 */
.settings-item-description::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(255, 255, 255, 0.3);
}

/* 固定啟用標籤 */
.settings-always-on {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  padding: 0 8px;
  border-radius: 10px;
  color: #00ABC2;
  font-family: 'Inter', var(--cjk-font), sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0%;
  text-align: center;
  vertical-align: middle;
  white-space: nowrap;
}

/* Toggle Switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 64px;
  height: 28px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.3);
  transition: 0.3s;
  border-radius: 100px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  width: 39px;
  height: 24px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  transition: 0.3s;
  border-radius: 100px;
}

.toggle-switch input:checked + .toggle-slider {
  background-color: #00ABC2;
}

.toggle-switch input:checked + .toggle-slider:before {
  transform: translateX(21px);
}

/* Save Button */
.settings-actions {
  display: none;
}

.save-settings-btn {
  display: none;
}
</style>
